/**
 * SmartSend v2 Environment Configuration
 * 
 * Centralized configuration management for environment-specific values.
 * All environment variables should be accessed through this file.
 * 
 * @module SmartSend_v2/env/config
 */

// ============================================
// ENVIRONMENT DETECTION
// ============================================

/**
 * Check if running in development mode
 * @returns {boolean}
 */
export const isDevelopment = () => process.env.NODE_ENV === 'development';

/**
 * Check if running in production mode
 * @returns {boolean}
 */
export const isProduction = () => process.env.NODE_ENV === 'production';

// ============================================
// RESEND CODE COOLDOWN
// ============================================

/**
 * Get resend code cooldown duration in seconds
 *
 * Priority:
 * 1. REACT_APP_RESEND_COOLDOWN_SECONDS (manual override)
 * 2. Environment-based default (10s dev, 300s prod)
 *
 * @returns {number} Cooldown duration in seconds
 */
export const getResendCooldownSeconds = () => {
  // Check for manual override
  const envValue = process.env.REACT_APP_RESEND_COOLDOWN_SECONDS;
  if (envValue && !isNaN(envValue)) {
    return parseInt(envValue, 10);
  }

  // Environment-based defaults
  return isDevelopment() ? 10 : 300;
};

/**
 * Get resend code cooldown duration in milliseconds
 * @returns {number} Cooldown duration in milliseconds
 */
export const getResendCooldownMs = () => {
  return getResendCooldownSeconds() * 1000;
};

// ============================================
// TIME TO LIVE (TTL) CONSTANTS
// ============================================

/**
 * Default TTL for localStorage items (24 hours)
 * @constant {number}
 */
export const DEFAULT_TTL = 24 * 60 * 60 * 1000;

/**
 * TTL for verification data (15 minutes)
 * Used for phone numbers and verification codes
 * @constant {number}
 */
export const VERIFICATION_TTL = 15 * 60 * 1000;

// ============================================
// RETRY LIMITS
// ============================================

/**
 * Maximum SMS send attempts per session
 * @constant {number}
 */
export const MAX_SMS_RETRIES = 5;

/**
 * Maximum code verification attempts per session
 * @constant {number}
 */
export const MAX_CODE_ATTEMPTS = 5;

// ============================================
// API CONFIGURATION
// ============================================

/**
 * NOTE: Backend API URLs are configured globally via src/settings.js
 * All SmartSend API services import `mainAPIUrl` from '../../../settings'
 * and construct endpoints as: `${mainAPIUrl}/gservices_3/ss/...`
 *
 * This keeps API configuration consistent across all Gesture Services projects.
 */

/**
 * Get Google Maps API key from environment
 * Used for Places autocomplete and timezone detection
 * @returns {string} Google API key
 */
export const getGoogleApiKey = () => {
  return process.env.REACT_APP_GOOGLE_API_KEY || '';
};

// ============================================
// ANALYTICS
// ============================================

/**
 * Segment analytics source identifier for SmartSend events
 * Used to filter and attribute SmartSend-originated events across all Segment destinations
 * @constant {string}
 */
export const ANALYTICS_SOURCE = 'smartsend';

// ============================================
// MEDIA & ASSETS
// ============================================

/**
 * Hero video URL for SmartSend intro video
 * @constant {string}
 */
export const HERO_VIDEO_URL =
  'https://firebasestorage.googleapis.com/v0/b/yourgestureapp.appspot.com/o/SmartSend_Intro_Video.mp4?alt=media&token=a85a382c-2979-44e8-8828-264657b1bb49';

/**
 * Gesture app download link (Branch deep link)
 * @constant {string}
 */
export const GESTURE_DOWNLOAD_URL = 'https://sendagesture.app.link/sHLaZtwfaZb';

// ============================================
// DEBUG & LOGGING
// ============================================

/**
 * Check if debug logging is enabled
 * @returns {boolean}
 */
export const isDebugEnabled = () => {
  return isDevelopment() || process.env.REACT_APP_DEBUG === 'true';
};

/**
 * Log config on initialization (development only)
 */
if (isDevelopment()) {
  console.group('[SmartSend Config]');
  console.log('Environment:', process.env.NODE_ENV);
  console.log('Resend Cooldown:', getResendCooldownSeconds(), 'seconds');
  console.log('Debug Enabled:', isDebugEnabled());
  console.groupEnd();
}

// ============================================
// EXPORT SUMMARY
// ============================================

/**
 * Get all config values as an object (useful for debugging)
 * @returns {Object} All configuration values
 */
export const getAllConfig = () => ({
  environment: {
    isDevelopment: isDevelopment(),
    isProduction: isProduction(),
    nodeEnv: process.env.NODE_ENV,
  },
  resendCooldown: {
    seconds: getResendCooldownSeconds(),
    milliseconds: getResendCooldownMs(),
  },
  ttl: {
    default: DEFAULT_TTL,
    verification: VERIFICATION_TTL,
  },
  retryLimits: {
    sms: MAX_SMS_RETRIES,
    code: MAX_CODE_ATTEMPTS,
  },
  debug: {
    enabled: isDebugEnabled(),
  },
});

